import type { MultiplayerConnectionStatus } from "../net/client.ts";
import type { ChatMessage, RoomDirectoryEntry } from "../net/protocol.ts";
import type { MultiplayerRoomSnapshot } from "../sim/session/types.ts";

export interface MultiplayerUiCallbacks {
  readonly onOpenChange: (open: boolean) => void;
  readonly onRefresh: () => Promise<readonly RoomDirectoryEntry[]>;
  readonly onCreate: (value: Readonly<{
    roomName: string;
    displayName: string;
    password: string;
  }>) => Promise<void>;
  readonly onJoin: (value: Readonly<{
    roomId: string;
    displayName: string;
    password: string;
  }>) => Promise<void>;
  readonly onSolo: () => void;
  readonly onChat: (text: string) => void;
  readonly onLeave: () => void;
}

export interface MultiplayerUi {
  isOpen(): boolean;
  close(): void;
  enterRoom(): void;
  enterSolo(): void;
  setRooms(rooms: readonly RoomDirectoryEntry[]): void;
  setStatus(status: MultiplayerConnectionStatus, detail?: string): void;
  setRoomSnapshot(snapshot: Readonly<MultiplayerRoomSnapshot>, localPlayerId: string): void;
  setChatHistory(messages: readonly ChatMessage[]): void;
  appendChat(message: Readonly<ChatMessage>): void;
  appendPresence(playerId: string, displayName: string, connected: boolean): void;
  showError(message: string): void;
  dispose(): void;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}

function labeledInput(
  labelText: string,
  name: string,
  type: "text" | "password" = "text",
  autocomplete = "off",
): Readonly<{ group: HTMLLabelElement; input: HTMLInputElement }> {
  const group = element("label", "multiplayer-field");
  const label = element("span", "multiplayer-field-label");
  label.textContent = labelText;
  const input = element("input", "multiplayer-input");
  input.name = name;
  input.type = type;
  input.setAttribute("autocomplete", autocomplete);
  group.append(label, input);
  return { group, input };
}

export function createMultiplayerUi(
  root: HTMLElement,
  callbacks: MultiplayerUiCallbacks,
): MultiplayerUi {
  const appRoot = root.closest<HTMLElement>("#app");
  const gameCanvas = appRoot?.querySelector<HTMLCanvasElement>("#game-canvas");

  const trigger = element("button", "multiplayer-trigger");
  trigger.type = "button";
  trigger.hidden = true;
  trigger.setAttribute("aria-label", "Open multiplayer room");
  trigger.title = "Multiplayer room (P)";
  trigger.setAttribute("aria-expanded", "false");
  trigger.innerHTML = '<span class="multiplayer-trigger-mark" aria-hidden="true">•••</span><span>Together</span><kbd>P</kbd>';

  const entry = element("section", "multiplayer-entry");
  entry.setAttribute("role", "dialog");
  entry.setAttribute("aria-modal", "true");
  entry.setAttribute("aria-labelledby", "multiplayer-entry-title");
  const entryShell = element("div", "multiplayer-entry-shell");
  const entryIntro = element("header", "multiplayer-entry-intro");
  const entryEyebrow = element("p", "multiplayer-entry-eyebrow");
  entryEyebrow.textContent = "Vilnius, together";
  const entryTitle = element("h1", "multiplayer-entry-title");
  entryTitle.id = "multiplayer-entry-title";
  entryTitle.textContent = "Meet before you wander.";
  const entryCopy = element("p", "multiplayer-entry-copy");
  entryCopy.textContent =
    "The city opens after you create a walk, join friends, or choose a quiet walk alone.";
  const entryFacts = element("ul", "multiplayer-entry-facts");
  for (const fact of ["Solo or 2–8 walkers", "One shared city", "Room-wide chat"]) {
    const item = element("li", "");
    item.textContent = fact;
    entryFacts.append(item);
  }
  entryIntro.append(entryEyebrow, entryTitle, entryCopy, entryFacts);

  const panel = element("section", "multiplayer-panel");
  panel.setAttribute("aria-label", "Multiplayer rooms");
  const panelHead = element("header", "multiplayer-panel-head");
  const titleWrap = element("div", "");
  const kicker = element("p", "multiplayer-kicker");
  kicker.textContent = "Small company";
  const title = element("h2", "multiplayer-title");
  title.textContent = "Meet in Vilnius";
  titleWrap.append(kicker, title);
  const close = element("button", "multiplayer-close");
  close.type = "button";
  close.hidden = true;
  close.setAttribute("aria-label", "Close multiplayer");
  close.textContent = "×";
  panelHead.append(titleWrap, close);

  const status = element("p", "multiplayer-status");
  status.dataset.state = "offline";
  status.textContent = "Create a room or join friends already walking.";
  const error = element("p", "multiplayer-error");
  error.hidden = true;
  error.setAttribute("role", "alert");

  const displayName = labeledInput("Your name", "display-name", "text", "nickname");
  displayName.input.maxLength = 24;
  displayName.input.value = localStorage.getItem("vilnius.multiplayer.display-name") ?? "";
  const soloButton = element("button", "multiplayer-solo");
  soloButton.type = "button";
  soloButton.textContent = "Walk alone";

  const tabs = element("div", "multiplayer-tabs");
  const browseTab = element("button", "multiplayer-tab is-active");
  browseTab.type = "button";
  browseTab.textContent = "Existing rooms";
  const createTab = element("button", "multiplayer-tab");
  createTab.type = "button";
  createTab.textContent = "Create a room";
  tabs.append(browseTab, createTab);

  const browseView = element("div", "multiplayer-view");
  const browseIntro = element("div", "multiplayer-view-intro");
  const browseNote = element("p", "multiplayer-note");
  browseNote.textContent = "Rooms are deliberately small: eight people at most.";
  const refresh = element("button", "multiplayer-text-button");
  refresh.type = "button";
  refresh.textContent = "Refresh";
  browseIntro.append(browseNote, refresh);
  const roomList = element("ol", "multiplayer-room-list");
  const empty = element("p", "multiplayer-empty");
  empty.textContent = "No rooms are open yet. You can be first to put the kettle on.";
  browseView.append(browseIntro, roomList, empty);

  const createView = element("form", "multiplayer-view multiplayer-create");
  createView.hidden = true;
  const roomName = labeledInput("Room name", "room-name", "text");
  roomName.input.maxLength = 48;
  roomName.input.placeholder = "Saturday walk";
  const createPassword = labeledInput("Password (optional)", "room-password", "password", "new-password");
  createPassword.input.maxLength = 72;
  const createSubmit = element("button", "multiplayer-primary");
  createSubmit.type = "submit";
  createSubmit.textContent = "Create and enter";
  createView.append(roomName.group, createPassword.group, createSubmit);

  panel.append(
    panelHead,
    status,
    error,
    soloButton,
    displayName.group,
    tabs,
    browseView,
    createView,
  );
  entryShell.append(entryIntro, panel);
  entry.append(entryShell);

  const chat = element("section", "room-chat");
  chat.hidden = true;
  chat.setAttribute("aria-label", "Room chat");
  const chatHead = element("header", "room-chat-head");
  const chatRoom = element("h2", "room-chat-title");
  chatHead.append(chatRoom);
  const members = element("p", "room-chat-members");
  const messages = element("ol", "room-chat-messages");
  messages.setAttribute("aria-live", "polite");
  const chatForm = element("form", "room-chat-form");
  const chatInput = element("input", "room-chat-input");
  chatInput.name = "chat";
  chatInput.maxLength = 200;
  chatInput.placeholder = "Say something to the room…";
  chatInput.autocomplete = "off";
  chatInput.setAttribute("aria-label", "Chat message");
  const chatSend = element("button", "room-chat-send");
  chatSend.type = "submit";
  chatSend.textContent = "Send";
  chatForm.append(chatInput, chatSend);
  chat.append(chatHead, members, messages, chatForm);

  const gameMenu = element("section", "game-menu");
  gameMenu.hidden = true;
  gameMenu.setAttribute("role", "dialog");
  gameMenu.setAttribute("aria-modal", "true");
  gameMenu.setAttribute("aria-labelledby", "game-menu-title");
  const gameMenuPanel = element("div", "game-menu-panel");
  const gameMenuEyebrow = element("p", "game-menu-eyebrow");
  gameMenuEyebrow.textContent = "Vilnius";
  const gameMenuTitle = element("h2", "game-menu-title");
  gameMenuTitle.id = "game-menu-title";
  gameMenuTitle.textContent = "Menu";
  const gameMenuRoom = element("p", "game-menu-room");
  const gameMenuNote = element("p", "game-menu-note");
  gameMenuNote.textContent = "The shared city keeps moving while this menu is open.";
  const gameMenuActions = element("div", "game-menu-actions");
  const continueButton = element("button", "game-menu-action game-menu-continue");
  continueButton.type = "button";
  continueButton.textContent = "Continue walking";
  const leaveButton = element("button", "game-menu-action game-menu-leave");
  leaveButton.type = "button";
  leaveButton.textContent = "Leave room";
  gameMenuActions.append(continueButton, leaveButton);
  const gameMenuHint = element("p", "game-menu-hint");
  gameMenuHint.innerHTML = '<kbd>Esc</kbd> return to the city';
  gameMenuPanel.append(
    gameMenuEyebrow,
    gameMenuTitle,
    gameMenuRoom,
    gameMenuNote,
    gameMenuActions,
    gameMenuHint,
  );
  gameMenu.append(gameMenuPanel);

  root.append(trigger, entry, chat, gameMenu);
  let open = false;
  let chatOpen = false;
  let gameMenuOpen = false;
  let reportedOverlayOpen = false;
  let rooms: readonly RoomDirectoryEntry[] = [];
  let selectedRoomId: string | null = null;
  let connected = false;
  let entered = false;

  appRoot?.classList.add("multiplayer-entry-required");
  gameCanvas?.setAttribute("aria-hidden", "true");

  function persistName(): string | null {
    const value = displayName.input.value.trim();
    if (value.length === 0) {
      showError("Add your name first.");
      displayName.input.focus();
      return null;
    }
    localStorage.setItem("vilnius.multiplayer.display-name", value);
    return value;
  }

  function hasOpenOverlay(): boolean {
    return open || chatOpen || gameMenuOpen;
  }

  function reportOverlayState(): void {
    const next = hasOpenOverlay();
    if (next === reportedOverlayOpen) return;
    reportedOverlayOpen = next;
    callbacks.onOpenChange(next);
  }

  function hideChat(): void {
    chatOpen = false;
    chat.hidden = true;
  }

  function hideGameMenu(): void {
    gameMenuOpen = false;
    gameMenu.hidden = true;
  }

  function setOpen(next: boolean): void {
    if (!entered && !next) return;
    if (open === next) return;
    if (next) {
      hideChat();
      hideGameMenu();
    }
    open = next;
    entry.hidden = !open;
    trigger.setAttribute("aria-expanded", String(open));
    reportOverlayState();
    if (open) {
      if (entered) close.focus();
      else displayName.input.focus();
      void refreshRooms();
    }
  }

  function setChatOpen(next: boolean): void {
    if (!entered || !connected) return;
    if (chatOpen === next) return;
    if (next) {
      open = false;
      entry.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      hideGameMenu();
    }
    chatOpen = next;
    chat.hidden = !chatOpen;
    reportOverlayState();
    if (chatOpen) {
      messages.scrollTop = messages.scrollHeight;
      chatInput.focus({ preventScroll: true });
    }
  }

  function setGameMenuOpen(next: boolean): void {
    if (!entered) return;
    if (gameMenuOpen === next) return;
    if (next) {
      open = false;
      entry.hidden = true;
      trigger.setAttribute("aria-expanded", "false");
      hideChat();
    }
    gameMenuOpen = next;
    gameMenu.hidden = !gameMenuOpen;
    reportOverlayState();
    if (gameMenuOpen) continueButton.focus({ preventScroll: true });
  }

  function closeOverlays(): void {
    if (!entered) return;
    open = false;
    entry.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    hideChat();
    hideGameMenu();
    reportOverlayState();
  }

  function showError(message: string): void {
    error.textContent = message;
    error.hidden = false;
  }

  async function refreshRooms(): Promise<void> {
    refresh.disabled = true;
    error.hidden = true;
    try {
      renderRooms(await callbacks.onRefresh());
    } catch (reason) {
      showError(reason instanceof Error ? reason.message : "Rooms could not be loaded.");
    } finally {
      refresh.disabled = false;
    }
  }

  function renderRooms(nextRooms: readonly RoomDirectoryEntry[]): void {
    rooms = nextRooms;
    roomList.replaceChildren();
    empty.hidden = rooms.length > 0;
    for (const room of rooms) {
      const item = element("li", "multiplayer-room");
      if (room.id === selectedRoomId) item.classList.add("is-selected");
      const copy = element("div", "multiplayer-room-copy");
      const name = element("strong", "multiplayer-room-name");
      name.textContent = room.name;
      const meta = element("span", "multiplayer-room-meta");
      meta.textContent = `${room.playerCount}/${room.capacity} people · ${room.passwordProtected ? "password" : "open"}`;
      copy.append(name, meta);
      const join = element("button", "multiplayer-room-join");
      join.type = "button";
      join.textContent = room.id === selectedRoomId ? "Join" : "Choose";
      join.addEventListener("click", () => {
        if (selectedRoomId !== room.id) {
          selectedRoomId = room.id;
          renderRooms(rooms);
          return;
        }
        const nameValue = persistName();
        if (nameValue === null) return;
        let password = "";
        if (room.passwordProtected) {
          const passwordInput = item.querySelector<HTMLInputElement>(".multiplayer-inline-password");
          password = passwordInput?.value ?? "";
        }
        join.disabled = true;
        void callbacks.onJoin({ roomId: room.id, displayName: nameValue, password })
          .catch((reason) => {
            join.disabled = false;
            showError(reason instanceof Error ? reason.message : "The room could not be joined.");
          });
      });
      item.append(copy, join);
      if (room.id === selectedRoomId && room.passwordProtected) {
        const password = element("input", "multiplayer-input multiplayer-inline-password");
        password.type = "password";
        password.placeholder = "Room password";
        password.autocomplete = "current-password";
        password.maxLength = 72;
        item.append(password);
        queueMicrotask(() => password.focus());
      }
      roomList.append(item);
    }
  }

  function renderMessage(message: Readonly<ChatMessage>): void {
    if (messages.querySelector(`[data-message-id="${CSS.escape(message.id)}"]`) !== null) return;
    const item = element("li", "room-chat-message");
    item.dataset.messageId = message.id;
    const author = element("strong", "room-chat-author");
    author.textContent = message.displayName;
    const body = element("span", "room-chat-body");
    body.textContent = message.text;
    item.append(author, body);
    messages.append(item);
    while (messages.childElementCount > 50) messages.firstElementChild?.remove();
    messages.scrollTop = messages.scrollHeight;
  }

  function renderPresence(
    playerId: string,
    displayNameValue: string,
    isConnected: boolean,
  ): void {
    const item = element("li", "room-chat-message room-chat-presence");
    item.dataset.playerId = playerId;
    const body = element("span", "room-chat-body");
    body.textContent = `${displayNameValue} ${isConnected ? "joined the walk" : "left the walk"}.`;
    item.append(body);
    messages.append(item);
    while (messages.childElementCount > 50) messages.firstElementChild?.remove();
    messages.scrollTop = messages.scrollHeight;
  }

  trigger.addEventListener("click", () => setOpen(!open));
  close.addEventListener("click", () => setOpen(false));
  refresh.addEventListener("click", () => void refreshRooms());
  soloButton.addEventListener("click", callbacks.onSolo);
  browseTab.addEventListener("click", () => {
    browseTab.classList.add("is-active");
    createTab.classList.remove("is-active");
    browseView.hidden = false;
    createView.hidden = true;
  });
  createTab.addEventListener("click", () => {
    createTab.classList.add("is-active");
    browseTab.classList.remove("is-active");
    browseView.hidden = true;
    createView.hidden = false;
    roomName.input.focus();
  });
  createView.addEventListener("submit", (event) => {
    event.preventDefault();
    const nameValue = persistName();
    if (nameValue === null || roomName.input.value.trim().length === 0) {
      if (roomName.input.value.trim().length === 0) roomName.input.focus();
      return;
    }
    createSubmit.disabled = true;
    error.hidden = true;
    void callbacks.onCreate({
      roomName: roomName.input.value.trim(),
      displayName: nameValue,
      password: createPassword.input.value,
    }).catch((reason) => {
      createSubmit.disabled = false;
      showError(reason instanceof Error ? reason.message : "The room could not be created.");
    });
  });
  chatForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const text = chatInput.value.trim();
    if (text.length === 0) return;
    callbacks.onChat(text);
    chatInput.value = "";
    setChatOpen(false);
  });
  continueButton.addEventListener("click", () => setGameMenuOpen(false));
  leaveButton.addEventListener("click", callbacks.onLeave);

  const onKeyDown = (event: KeyboardEvent): void => {
    const target = event.target;
    const editable = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
    if (event.code === "Escape") {
      if (chatOpen) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setChatOpen(false);
      } else if (open) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setOpen(false);
      } else if (gameMenuOpen) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setGameMenuOpen(false);
      } else if (!editable && entered && !event.repeat) {
        // Other overlays register their Escape handlers after this room UI.
        // Wait until dispatch completes so their preventDefault can win before
        // opening the general game menu.
        queueMicrotask(() => {
          if (!event.defaultPrevented && !hasOpenOverlay()) {
            setGameMenuOpen(true);
          }
        });
      }
      return;
    }
    if (editable) return;
    if (event.code === "KeyT" && connected && entered && !event.repeat) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setChatOpen(true);
    } else if (event.code === "KeyP") {
      event.preventDefault();
      setOpen(!open);
    }
  };
  window.addEventListener("keydown", onKeyDown);
  setOpen(true);

  return {
    isOpen: hasOpenOverlay,
    close: closeOverlays,
    setRooms: renderRooms,
    setStatus(nextStatus, detail): void {
      status.dataset.state = nextStatus;
      status.textContent = detail ?? (
        nextStatus === "offline" ? "Create a room or join friends already walking." :
        nextStatus === "connecting" ? "Opening the room…" :
        nextStatus === "reconnecting" ? "The line went quiet. Rejoining…" :
        nextStatus === "connected" ? "Everyone here shares the same city state." :
        "The room connection needs attention."
      );
      connected = nextStatus === "connected" || nextStatus === "reconnecting";
      if (!connected) {
        hideChat();
        reportOverlayState();
      }
      trigger.classList.toggle("is-connected", connected);
    },
    enterRoom(): void {
      if (entered) return;
      entered = true;
      entry.classList.add("is-entered");
      entry.removeAttribute("aria-modal");
      trigger.hidden = false;
      close.hidden = false;
      appRoot?.classList.remove("multiplayer-entry-required");
      gameCanvas?.removeAttribute("aria-hidden");
      setOpen(false);
    },
    enterSolo(): void {
      if (entered) return;
      entered = true;
      entry.classList.add("is-entered");
      entry.removeAttribute("aria-modal");
      trigger.hidden = false;
      close.hidden = false;
      gameMenuRoom.textContent = "Walking alone";
      gameMenuNote.textContent = "The city waits while this menu is open.";
      leaveButton.textContent = "Return to start";
      appRoot?.classList.remove("multiplayer-entry-required");
      gameCanvas?.removeAttribute("aria-hidden");
      setOpen(false);
    },
    setRoomSnapshot(snapshot, localPlayerId): void {
      chatRoom.textContent = snapshot.roomName;
      const roomMembers = Object.values(snapshot.membersByPlayerId);
      const online = roomMembers.filter((member) => member.connected).length;
      const localName = snapshot.membersByPlayerId[localPlayerId]?.displayName;
      members.textContent = `${online} online · you are ${localName ?? "here"}`;
      gameMenuRoom.textContent = `${snapshot.roomName} · ${online} online`;
    },
    setChatHistory(history): void {
      messages.replaceChildren();
      for (const message of history) renderMessage(message);
    },
    appendChat: renderMessage,
    appendPresence: renderPresence,
    showError,
    dispose(): void {
      window.removeEventListener("keydown", onKeyDown);
      trigger.remove();
      entry.remove();
      chat.remove();
      gameMenu.remove();
      appRoot?.classList.remove("multiplayer-entry-required");
      gameCanvas?.removeAttribute("aria-hidden");
    },
  };
}
